/**
 * C4.5: Grafico 2 — Confronto filtri incrociabili
 * Dual comparison: 2 territori o 2 segmenti affiancati
 */
import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/Card";
import type { IstatPresenzeMensili } from "../../types/database";

const MONTH_LABELS = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];

interface ComparisonChartProps {
  seriesA: IstatPresenzeMensili[];
  seriesB: IstatPresenzeMensili[];
  labelA: string;
  labelB: string;
}

export function ComparisonChart({ seriesA, seriesB, labelA, labelB }: ComparisonChartProps) {
  const data = useMemo(() => {
    const aByMonth = Array.from({ length: 12 }, (_, i) => {
      const found = seriesA.find(p => p.mese === i + 1);
      return found?.presenze || 0;
    });
    const bByMonth = Array.from({ length: 12 }, (_, i) => {
      const found = seriesB.find(p => p.mese === i + 1);
      return found?.presenze || 0;
    });
    const max = Math.max(...aByMonth, ...bByMonth, 1);
    return { aByMonth, bByMonth, max };
  }, [seriesA, seriesB]);

  if (seriesA.length === 0 && seriesB.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          Seleziona due territori o segmenti per il confronto
        </CardContent>
      </Card>
    );
  }

  const { aByMonth, bByMonth, max } = data;
  const svgW = 700;
  const svgH = 300;
  const padL = 60;
  const padR = 20;
  const padT = 20;
  const padB = 40;
  const plotW = svgW - padL - padR;
  const plotH = svgH - padT - padB;
  const groupW = plotW / 12;
  const barW = groupW * 0.35;

  const scaleY = (v: number) => padT + plotH - (v / max) * plotH;
  const scaleX = (i: number) => padL + (i + 0.5) * groupW;

  function formatNum(n: number): string {
    if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
    if (n >= 1_000) return (n / 1_000).toFixed(0) + "K";
    return String(n);
  }

  // Tooltip data
  const tooltipData = MONTH_LABELS.map((label, i) => {
    const aVal = aByMonth[i];
    const bVal = bByMonth[i];
    const aTotal = aByMonth.reduce((s, v) => s + v, 0);
    const bTotal = bByMonth.reduce((s, v) => s + v, 0);
    return {
      label,
      aVal, bVal,
      aPct: aTotal > 0 ? ((aVal / aTotal) * 100).toFixed(1) : "0",
      bPct: bTotal > 0 ? ((bVal / bTotal) * 100).toFixed(1) : "0",
    };
  });

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Confronto</CardTitle>
      </CardHeader>
      <CardContent>
        <svg viewBox={`0 0 ${svgW} ${svgH}`} className="w-full">
          {/* Grid */}
          {[0, 0.25, 0.5, 0.75, 1].map(frac => {
            const y = padT + plotH * (1 - frac);
            return (
              <g key={frac}>
                <line x1={padL} y1={y} x2={svgW - padR} y2={y} stroke="#e5e7eb" strokeWidth={0.5} />
                <text x={padL - 6} y={y + 4} textAnchor="end" fontSize={10} fill="#9ca3af">
                  {formatNum(max * frac)}
                </text>
              </g>
            );
          })}

          {/* Bars */}
          {aByMonth.map((aVal, i) => {
            const bVal = bByMonth[i];
            const cx = scaleX(i);
            const aH = (aVal / max) * plotH;
            const bH = (bVal / max) * plotH;
            return (
              <g key={i}>
                <rect
                  x={cx - barW - 1}
                  y={scaleY(aVal)}
                  width={barW}
                  height={aH}
                  fill="#284899"
                  rx={2}
                  opacity={0.8}
                />
                <rect
                  x={cx + 1}
                  y={scaleY(bVal)}
                  width={barW}
                  height={bH}
                  fill="#e07b39"
                  rx={2}
                  opacity={0.8}
                />
              </g>
            );
          })}

          {/* X labels */}
          {MONTH_LABELS.map((label, i) => (
            <text key={i} x={scaleX(i)} y={svgH - 8} textAnchor="middle" fontSize={11} fill="#6b7280">
              {label}
            </text>
          ))}
        </svg>

        {/* Legend */}
        <div className="flex items-center gap-6 mt-3 justify-center text-xs">
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded" style={{ backgroundColor: "#284899" }} />
            {labelA}
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded" style={{ backgroundColor: "#e07b39" }} />
            {labelB}
          </span>
        </div>

        {/* Summary table */}
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b text-muted-foreground">
                <th className="text-left py-1">Mese</th>
                <th className="text-right py-1">{labelA}</th>
                <th className="text-right py-1">%</th>
                <th className="text-right py-1">{labelB}</th>
                <th className="text-right py-1">%</th>
              </tr>
            </thead>
            <tbody>
              {tooltipData.map(d => (
                <tr key={d.label} className="border-b border-border/50">
                  <td className="py-1">{d.label}</td>
                  <td className="text-right">{formatNum(d.aVal)}</td>
                  <td className="text-right text-muted-foreground">{d.aPct}%</td>
                  <td className="text-right">{formatNum(d.bVal)}</td>
                  <td className="text-right text-muted-foreground">{d.bPct}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
